import { useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { MenuTitles } from 'constants/menu'
import { ContentLayout, PageLayout } from 'layout'
import { useParams } from 'react-router-dom'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import {
  AgxButton,
  AgxRow,
  AgxColumn,
  AgxBodyText,
  AgxHeader,
  AgxSortableList,
  Images,
} from '@urbanx/agx-ui-components'
import { AgxLoadingState } from '@urbanx/agx.ui-components2'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  useGetAgencySuppliers,
  useInvalidateUseGetAgencySuppliers,
  useInvalidateUseStaffGetAgentsInAgency,
} from '@urbanx/agx.agencies.client'
import { useAgentsInAgency, useSelectedAgency } from 'hooks'
import {
  MarketingItemTemplateModel,
  MarketingPackageTemplateModel,
  useCreateMarketingPackageTemplate,
  useDeleteMarketingPackageTemplate,
  useInvalidateUseStaffGetMarketingPackageTemplates,
  useStaffGetMarketingItemTemplates,
  useStaffGetMarketingPackageTemplates,
  useUpdateMarketingPackageTemplateOrder,
} from '@urbanx/agx.marketing.client'
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation'
import MarketingItems from './components/marketing-items/MarketingItems'
import MarketingPackages from './components/marketing-packages/marketingPackages'
import AgxAgencySuppliers from './components/agency-suppliers/AgencySuppliers'
import './marketing.scss'

const MarketingPage = () => {
  const { agencyId } = useParams()
  const { userAccount } = useContext(AuthContext)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showMoreOptionsId, setShowMoreOptionsId] = useState('')
  const [showMarketingPackages, setShowMarketingPackages] = useState(false)
  const [showSuppliers, setShowSuppliers] = useState(false)
  const [showMarketingItems, setShowMarketingItems] = useState(false)
  const [currentMarketingPackages, setMarketingPackages] = useState<
    MarketingPackageTemplateModel[]
  >([])
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null)
  const { data: agents, isPending: isPendingAgents } = useAgentsInAgency()
  const { agency: selectedAgency, isLoading: isLoadingSelectedAgency } =
    useSelectedAgency()

  const invalidateUseStaffGetMarketingPackageTemplates =
    useInvalidateUseStaffGetMarketingPackageTemplates()
  const invalidateUseGetAgencySuppliers = useInvalidateUseGetAgencySuppliers()
  const invalidateUseStaffGetAgentsInAgency =
    useInvalidateUseStaffGetAgentsInAgency()

  const {
    createMarketingPackageTemplate,
    createMarketingPackageTemplatePending,
  } = useCreateMarketingPackageTemplate({
    onSuccess: () => {
      invalidateUseStaffGetMarketingPackageTemplates(agencyId || '')
      setShowMoreOptionsId('')
    },
    onError: () => {},
  })

  const {
    updateMarketingPackageTemplateOrder,
    updateMarketingPackageTemplateOrderPending,
  } = useUpdateMarketingPackageTemplateOrder({
    onSuccess: () => {
      invalidateUseStaffGetMarketingPackageTemplates(agencyId || '')
    },
    onError: () => {},
  })

  const {
    deleteMarketingPackageTemplate,
    deleteMarketingPackageTemplatePending,
  } = useDeleteMarketingPackageTemplate({
    onSuccess: () => {
      invalidateUseStaffGetMarketingPackageTemplates(agencyId || '')
      setShowDeleteConfirmation(false)
    },
    onError: () => {},
  })

  const { data: marketingPackages, isPending: isLoadingMarketingPackages } =
    useStaffGetMarketingPackageTemplates(agencyId || '', !!agencyId)

  const { data: marketingItems, isPending: isLoadingMarketingItems } =
    useStaffGetMarketingItemTemplates(agencyId || '', !!agencyId)

  const { data: agencySuppliers, isPending: isLoadingAgencySuppliers } =
    useGetAgencySuppliers(agencyId || '', !!agencyId)

  useEffect(() => {
    if (marketingPackages) setMarketingPackages(marketingPackages)
  }, [marketingPackages, isLoadingMarketingPackages])

  useEffect(() => {
    if (showSuppliers) return

    invalidateUseGetAgencySuppliers(agencyId || '')
  }, [showSuppliers])

  useEffect(() => {
    invalidateUseStaffGetAgentsInAgency(agencyId || '')
  }, [agencyId, invalidateUseStaffGetAgentsInAgency])

  const onEditMarketingPackage = useCallback((mp: MarketingPackageTemplateModel) => {
    setSelectedPackageId(mp.id ?? null)
    setShowMarketingPackages(true)
  }, [])

  const onDeletePackageClick = useCallback((mp: MarketingPackageTemplateModel) => {
    setSelectedPackageId(mp.id ?? null)
    setShowDeleteConfirmation(true)
  }, [])

  const onDuplicatePackageClick = async (mp: MarketingPackageTemplateModel) => {
    await createMarketingPackageTemplate({
      agencyId: agencyId || '',
      order: currentMarketingPackages.length,
      name: `${mp.name} - Copy`,
      displayName: mp.displayName,
      includeRealEstateListing: mp.includeRealEstateListing,
      includeDomainListing: mp.includeDomainListing,
      displayItemisedPricingOnPdf: mp.displayItemisedPricingOnPdf,
      items: mp.items,
      addOns: mp.addOns,
      specialConditions: mp.specialConditions,
      assignedAgentIds: mp.assignedAgentIds,
    })
  }

  const onConfirmDelete = async () => {
    await deleteMarketingPackageTemplate({
      agencyId: selectedAgency?.id || '',
      marketingPackageId: selectedPackageId || '',
    })
  }

  const onAddNewPackageClick = useCallback(() => {
    setSelectedPackageId(null)
    setShowMarketingPackages(true)
  }, [])

  const handleOnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setMarketingPackages((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id)
      const newIndex = prev.findIndex((item) => item.id === over.id)
      const updatedPackages = arrayMove(prev, oldIndex, newIndex)

      const marketingPackageIds = updatedPackages
        .map((item) => item.id)
        .filter((id): id is string => !!id)

      updateMarketingPackageTemplateOrder({
        marketingPackageIds,
      })

      return updatedPackages
    })
  }

  const onMarketingItems = () => {
    setShowMarketingItems(true)
  }

  const convertMarketingItemTemplateToIMarketingItemTemplateModel = (
    marketingItemTemplates: MarketingItemTemplateModel[]
  ) => {
    return marketingItemTemplates.map(
      (item) =>
        ({
          id: item.id,
          //agencyId: item.agencyId,
          productName: item.productName,
          pricePerUnit: item.pricePerUnit,
          itemType: item.itemType,
          supplierId: item.supplierId,
          description: item.description,
          category: item.category,
          order: item.order,
          templateType: item.templateType,
          groupItems: item.groupItems,
        }) as MarketingItemTemplateModel
    )
  }

  const renderMarketingPackages = (
    mp: MarketingPackageTemplateModel,
    index: number
  ) => {
    return (
      <AgxRow
        spaceBetween
        fill
        centered
        key={mp.id || `marketingItem-${mp.id}-${index}`}
        id={mp.id || `marketingItem-${mp.id}-${index}`}
        extraClasses={'marketingPackageItem'}
      >
        <AgxHeader size={4}>{mp.name}</AgxHeader>
        <div className="actionContainerTD">
          <AgxButton
            text="Manage"
            medium
            hollow
            disabled={
              isLoadingMarketingItems ||
              isLoadingMarketingPackages ||
              isLoadingSelectedAgency ||
              createMarketingPackageTemplatePending ||
              updateMarketingPackageTemplateOrderPending ||
              deleteMarketingPackageTemplatePending
            }
            onClick={() => onEditMarketingPackage(mp)}
          />
          <div className="moreOptions">
            <AgxButton
              text=""
              large
              naked
              leftIcon={<Images.More />}
              onClick={() => {
                mp.id && showMoreOptionsId !== mp.id
                  ? setShowMoreOptionsId(mp.id)
                  : setShowMoreOptionsId('')
              }}
            />
            {showMoreOptionsId.length > 0 && showMoreOptionsId === mp.id && (
              <div className="moreOptionsContent">
                <div onClick={() => onDuplicatePackageClick(mp)}>Duplicate</div>
                <div onClick={() => onDeletePackageClick(mp)}>Delete</div>
              </div>
            )}
          </div>
        </div>
      </AgxRow>
    )
  }

  const activeAgents = useMemo(() => {
    return agents?.filter((a) => !a.softDeleted) || []
  }, [agents])

  const selectedPackage = useMemo(() => {
    if (!selectedPackageId) {
      return undefined
    }

    return currentMarketingPackages.find((pkg) => pkg.id === selectedPackageId)
  }, [currentMarketingPackages, selectedPackageId])

  const isMarketingPackageEditorLoading = useMemo(() => {
    if (!showMarketingPackages) {
      return false
    }

    if (isLoadingMarketingItems || isPendingAgents || isLoadingSelectedAgency) {
      return true
    }

    if (selectedPackageId && isLoadingMarketingPackages) {
      return true
    }

    if (selectedPackageId && !selectedPackage) {
      return true
    }

    return false
  }, [
    isLoadingMarketingItems,
    isLoadingMarketingPackages,
    isLoadingSelectedAgency,
    isPendingAgents,
    selectedPackage,
    selectedPackageId,
    showMarketingPackages,
  ])

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.MARKETING}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.MARKETING}
      >
        <AgxRow extraClasses="stretchContainerItems">
          {showMarketingItems &&
            !isLoadingAgencySuppliers &&
            agencySuppliers && (
              <MarketingItems
                onBack={() => setShowMarketingItems(false)}
                existingItems={convertMarketingItemTemplateToIMarketingItemTemplateModel(
                  marketingItems ?? []
                )}
                agencyId={selectedAgency?.id || ''}
                agencySuppliers={agencySuppliers.suppliers ?? []}
              />
            )}
          {showMarketingPackages && isMarketingPackageEditorLoading && (
            <AgxLoadingState />
          )}
          {showMarketingPackages && !isMarketingPackageEditorLoading && (
            <MarketingPackages
              onBack={() => setShowMarketingPackages(false)}
              marketingPackage={selectedPackage}
              marketingPackageOrder={
                !selectedPackage
                  ? currentMarketingPackages?.length + 1
                  : (selectedPackage.order ?? 0)
              }
              includedStandardInclusions={selectedPackage?.items || []}
              includedAddons={selectedPackage?.addOns || []}
              allItems={marketingItems || []}
              allAvailableAgents={activeAgents || []}
              agencyId={selectedAgency?.id || ''}
            />
          )}
          {showSuppliers && agencySuppliers && (
            <AgxAgencySuppliers
              agencySuppliers={agencySuppliers}
              onBack={() => setShowSuppliers(false)}
            />
          )}
          {!showMarketingItems && !showMarketingPackages && !showSuppliers && (
            <AgxColumn fill>
              <AgxRow fill veryLargeGap>
                <AgxColumn fill extraClasses="sectionStyle">
                  <AgxHeader size={2}>{marketingItems?.length}</AgxHeader>
                  <AgxColumn extraClasses="borderBottomContainer noBorder">
                    <AgxBodyText large>Marketing Items</AgxBodyText>
                  </AgxColumn>
                  <AgxRow>
                    <AgxButton
                      text="View Items"
                      medium
                      primary
                      rightIcon={<Images.ListOutlineWhite />}
                      onClick={() => onMarketingItems()}
                      disabled={
                        isLoadingMarketingItems ||
                        isLoadingAgencySuppliers ||
                        isLoadingSelectedAgency
                      }
                    />
                  </AgxRow>
                </AgxColumn>
                <AgxColumn fill extraClasses="sectionStyle">
                  <AgxHeader size={2}>
                    {agencySuppliers?.suppliers?.length ?? 0}
                  </AgxHeader>
                  <AgxColumn extraClasses="borderBottomContainer noBorder">
                    <AgxBodyText large>Suppliers</AgxBodyText>
                  </AgxColumn>
                  <AgxRow>
                    <AgxButton
                      text="View Suppliers"
                      medium
                      primary
                      rightIcon={<Images.ListOutlineWhite />}
                      onClick={() => setShowSuppliers(true)}
                      disabled={isLoadingAgencySuppliers}
                    />
                  </AgxRow>
                </AgxColumn>
              </AgxRow>
              <AgxRow fill>
                <AgxColumn fill extraClasses="sectionStyle">
                  <AgxHeader size={2}>
                    {currentMarketingPackages?.length}
                  </AgxHeader>
                  <AgxColumn extraClasses="borderBottomContainer">
                    <AgxBodyText large>Marketing Packages</AgxBodyText>
                  </AgxColumn>
                  <AgxSortableList
                    items={currentMarketingPackages}
                    getId={(item) => item.id || `marketingItem-${item.id}`}
                    renderItem={(item, index) =>
                      renderMarketingPackages(item, index)
                    }
                    onDragEnd={handleOnDragEnd}
                    extraClasses="marketingPackagesListItems"
                  />
                  <AgxRow extraClasses="marketingPackageItem">
                    <AgxButton
                      text="New Package"
                      medium
                      primary
                      rightIcon={<Images.ListOutlineWhite />}
                      onClick={() => onAddNewPackageClick()}
                      disabled={
                        isLoadingMarketingPackages ||
                        isLoadingMarketingItems ||
                        isLoadingSelectedAgency ||
                        isPendingAgents
                      }
                    />
                  </AgxRow>
                </AgxColumn>
              </AgxRow>
            </AgxColumn>
          )}

          <DeleteConfirmation
            name={selectedPackage?.name || ''}
            title="Delete marketing package?"
            lineOne="Are you sure you want to delete"
            lineTwo="This cannot be undone."
            primaryActionTitle="Permanently delete package"
            secondaryActionTitle="No, cancel"
            showPopUp={showDeleteConfirmation}
            onClose={() => setShowDeleteConfirmation(false)}
            onConfirm={() => onConfirmDelete()}
          />
        </AgxRow>
      </ContentLayout>
    </PageLayout>
  )
}

export default MarketingPage
